import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../config';
import type { Post, CreatePostData } from './types';

function removeUndefinedDeep(value: any): any {
  if (Array.isArray(value)) {
    return value.map(removeUndefinedDeep).filter(v => v !== undefined);
  }
  if (value && typeof value === 'object') {
    const out: Record<string, any> = {};
    Object.keys(value).forEach((k) => {
      const v = removeUndefinedDeep((value as any)[k]);
      if (v !== undefined) out[k] = v;
    });
    return out;
  }
  return value === undefined ? undefined : value;
}

export async function createPost(userId: string, data: CreatePostData): Promise<string> {
  try {
    const postsRef = collection(db, 'posts');
    const postData = {
      ...data,
      userId,
      stats: {
        likes: 0,
        comments: 0,
        impressions: 0
      },
      likes: [],
      likedBy: [],
      images: data.images || [],
      videoUrl: data.videoUrl || '',
      videoUpload: data.videoUpload || '',
      publishingOptions: {
        access: data.publishingOptions?.access || 'paid_members',
        price: data.publishingOptions?.price || '',
        earlyAccess: data.publishingOptions?.earlyAccess || false,
        releaseDate: data.publishingOptions?.releaseDate || null,
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(postsRef, postData);
    return docRef.id;
  } catch (error: any) {
    console.error('[createPost] Error:', error);
    throw new Error(error.message || 'Failed to create post');
  }
}

export async function updatePost(postId: string, data: Partial<Post>): Promise<void> {
  if (!postId) {
    console.error('[updatePost] No post ID provided');
    throw new Error('No post ID provided');
  }

  try {
    const postRef = doc(db, 'posts', postId);
    const updateData: Record<string, any> = {};

    // Only include defined fields and ensure arrays are initialized
    if (typeof data.title === 'string') updateData.title = data.title;
    if (typeof data.content === 'string') updateData.content = data.content;
    if (data.type) updateData.type = data.type;
    if (typeof data.videoUrl === 'string') updateData.videoUrl = data.videoUrl;
    if (typeof data.videoUpload === 'string') updateData.videoUpload = data.videoUpload;
    if (data.images) updateData.images = Array.isArray(data.images) ? data.images : [];
    if (data.publishingOptions) updateData.publishingOptions = removeUndefinedDeep(data.publishingOptions);
    if (typeof data.isLocked === 'boolean') updateData.isLocked = data.isLocked;
    if (typeof data.isPublished === 'boolean') updateData.isPublished = data.isPublished;
    if (data.stats) updateData.stats = data.stats;

    // Always update the timestamp
    updateData.updatedAt = serverTimestamp();

    await updateDoc(postRef, removeUndefinedDeep(updateData));
  } catch (error: any) {
    console.error('[updatePost] Error:', error);
    throw new Error(error?.message || error?.code || 'Failed to update post');
  }
}

export async function deletePost(postId: string): Promise<void> {
  try {
    // First, delete all likes associated with this post
    const likesRef = collection(db, 'likes');
    const likesQuery = query(likesRef, where('postId', '==', postId));
    const likesSnapshot = await getDocs(likesQuery);

    // Delete all likes in parallel
    const likeDeletions = likesSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(likeDeletions);

    // Then delete the post itself
    await deleteDoc(doc(db, 'posts', postId));
  } catch (error: any) {
    console.error('Error deleting post:', error);
    throw new Error(error.message || 'Failed to delete post');
  }
}

export async function getUserPosts(userId: string): Promise<Post[]> {
  try {
    const postsRef = collection(db, 'posts');
    const q = query(
      postsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Post[];
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch posts');
  }
}

export async function togglePinPost(postId: string, isPinned: boolean): Promise<void> {
  try {
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      isPinned,
      updatedAt: serverTimestamp(),
    });
  } catch (error: any) {
    throw new Error(error.message || 'Failed to pin/unpin post');
  }
}

export async function getPostById(postId: string): Promise<Post | null> {
  try {
    const postRef = doc(db, 'posts', postId);
    const postSnap = await getDoc(postRef);
    if (!postSnap.exists()) return null;
    return { id: postSnap.id, ...postSnap.data() } as Post;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch post');
  }
}